import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isTop = !scrolled;
  const textClass = isTop ? 'text-white' : 'text-foreground-700';
  const textHoverClass = isTop ? 'hover:text-white/80' : 'hover:text-primary-500';
  const brandClass = isTop ? 'text-white' : 'text-foreground-950';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-gradient-to-b from-black/55 to-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="max-w-[1200px] mx-auto flex items-center justify-between h-16 md:h-[68px]">
            <Link
              to="/"
              className={`flex items-center gap-2 font-heading text-xl md:text-2xl font-bold tracking-tight ${brandClass}`}
            >
              pandabuy<span className={isTop ? 'text-primary-300' : 'text-primary-500'}>.skin</span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-semibold ${textClass} ${textHoverClass} transition-colors whitespace-nowrap`}>
                Home
              </Link>

              <Link to="/article/pandabuy-spreadsheet-guide" className={`text-sm font-semibold ${textClass} ${textHoverClass} transition-colors whitespace-nowrap`}>
                Shopping Guide
              </Link>

              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1 text-sm font-semibold ${textClass} ${textHoverClass} transition-colors whitespace-nowrap cursor-pointer`}
                >
                  Categories
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 rounded-2xl shadow-lg border border-background-200/70 py-3 w-[520px] z-50">
                    <div className="grid grid-cols-2 gap-1 px-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                            <i className={cat.icon}></i>
                          </span>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to="#articles" className={`text-sm font-semibold ${textClass} ${textHoverClass} transition-colors whitespace-nowrap`}>
                Articles
              </Link>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  isTop
                    ? 'bg-white/15 backdrop-blur-sm border border-white/25 text-white hover:bg-white/30'
                    : 'border border-foreground-300/50 text-foreground-700 hover:border-primary-400 hover:text-primary-500'
                }`}
              >
                Pandabuy Sheet
                <i className="ri-external-link-line text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-line"></i>
              </a>

              <button
                onClick={() => setMobileOpen(true)}
                className={`md:hidden w-9 h-9 flex items-center justify-center text-xl ${textClass} cursor-pointer`}
                aria-label="Open menu"
              >
                <i className="ri-menu-line"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-0 right-0 w-[85%] max-w-[360px] h-full bg-background-50 overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-background-200/60">
              <Link to="/" className="font-heading text-lg font-bold text-foreground-950" onClick={() => setMobileOpen(false)}>
                pandabuy<span className="text-primary-500">.skin</span>
              </Link>
              <button onClick={() => setMobileOpen(false)} className="w-9 h-9 flex items-center justify-center text-xl text-foreground-600 cursor-pointer" aria-label="Close menu">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-5 space-y-1">
              <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors font-medium text-sm">
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-home-4-line"></i></span>
                Home
              </Link>
              <Link to="/article/pandabuy-spreadsheet-guide" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors font-medium text-sm">
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-book-open-line"></i></span>
                Shopping Guide
              </Link>

              <div className="pt-3 pb-1 px-3">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Categories</p>
              </div>

              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.name}
                </a>
              ))}

              <div className="pt-4 border-t border-background-200/60 mt-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-all"
                >
                  Buy It Now
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 mt-3 rounded-xl border border-foreground-300/50 text-foreground-700 font-semibold text-sm hover:border-primary-400 hover:text-primary-500 transition-all"
                >
                  Pandabuy Sheet
                  <i className="ri-external-link-line text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}